/**
 * Authenticated API client.
 *
 * Same-origin (Caddy serves the SPA at ``/`` and proxies ``/api`` / ``/auth`` /
 * ``/health`` / ``/ready`` to the FastAPI backend on :8000). No baseURL needed.
 *
 * BUG-010 prevention: ``formatValidationError(err)`` extracts a readable message
 * from any of the three FastAPI 4xx detail shapes — never returns ``[object Object]``.
 *
 * AUTH-05: a 401 from any endpoint clears the auth store AND redirects to
 * ``/login?next=<originalPath>`` so the user lands back where they were after sign-in.
 */
import { ApiError } from "@/types/api";
import type { ApiErrorBody } from "@/types/api";
import { useAuthStore } from "@/lib/auth";

export async function apiFetch<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...opts, headers });

  if (res.status === 401) {
    // AUTH-05: clear local state + preserve target URL for post-login redirect.
    useAuthStore.getState().logout();
    if (typeof window !== "undefined") {
      const current = window.location.pathname + window.location.search;
      // Don't loop if we're already on /login.
      if (!window.location.pathname.startsWith("/login")) {
        const next = encodeURIComponent(current);
        window.location.href = `/login?next=${next}`;
      }
    }
    const body: ApiErrorBody = await res.json().catch(() => ({
      detail: "unauthenticated",
    }));
    throw new ApiError(401, body, res.headers.get("x-request-id") ?? undefined);
  }

  if (!res.ok) {
    const body: ApiErrorBody = await res
      .json()
      .catch(() => ({ detail: "api_error" }));
    throw new ApiError(
      res.status,
      body,
      res.headers.get("x-request-id") ?? undefined,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * BUG-010: render a readable error string from any thrown value.
 *
 * Handles the four shapes this app sees:
 *   1. ``ApiError`` with string detail → returns the string verbatim
 *   2. ``ApiError`` with FastAPI 422 array → joins ``field: msg`` pairs with ``"; "``
 *   3. ``ApiError`` with composite object (``{error, missing}``) → ``"error (missing1, missing2)"``
 *   4. Plain ``Error`` (network, etc.) → returns ``err.message``
 *   5. Anything else → ``"Unknown error"``
 */
export function formatValidationError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : "Unknown error";
  }
  const detail = err.body.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        // FastAPI's ``loc`` typically starts with "body" — drop it for readability.
        const path = e.loc.slice(1).join(".") || e.loc.join(".");
        return path ? `${path}: ${e.msg}` : e.msg;
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    if ("error" in detail && detail.error !== undefined) {
      const errStr = String(detail.error);
      const missing =
        "missing" in detail && Array.isArray(detail.missing)
          ? ` (${detail.missing.join(", ")})`
          : "";
      return errStr + missing;
    }
  }
  return `HTTP ${err.status}`;
}
